import logo from "@/assets/images/logo.png";
import { useLanguage } from "@/hooks/useLanguage";
import { motion, useMotionValueEvent, useScroll } from "framer-motion";
import { Fragment, useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import Globe from "./Globe";

function Layout({ openModal }: { openModal: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { scrollY } = useScroll();
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [prevScrollY, setPrevScrollY] = useState(0);
  const { changeLanguage, isKorean } = useLanguage();
  const [isMobile, setIsMobile] = useState(false);

  // contact-us-button을 메인 페이지에서만 보이도록 하는 조건
  const showContactButton =
    location.pathname === "/kr" || location.pathname === "/en";

  const handleLanguageChange = (language: "ko" | "en") => {
    if (language === "ko") {
      changeLanguage("ko");
    } else {
      changeLanguage("en");
    }
  };

  useEffect(() => {
    setIsMobile(window.innerWidth < 1024);
  }, []);

  useMotionValueEvent(scrollY, "change", (latest) => {
    // 스크롤 변화량이 너무 작으면 무시 (바운스 스크롤 효과 방지)
    if (Math.abs(latest - prevScrollY) < 5) {
      setPrevScrollY(latest);
      return;
    }

    // 문서 높이와 창 높이를 고려하여 실제 스크롤 가능 여부 확인
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const maxScroll = documentHeight - windowHeight;

    // 바운스 스크롤 상태인지 확인 (iOS에서 주로 발생)
    const isBounceScroll = latest < 0 || latest > maxScroll;

    // 바운스 스크롤이 아니고 스크롤 방향이 아래로 내려갈 때만 애니메이션 트리거
    if (!isBounceScroll && latest > prevScrollY) {
      setShouldAnimate(true);
    } else if (!isBounceScroll && latest < prevScrollY) {
      // 스크롤 방향이 위로 올라갈 때 리셋
      setShouldAnimate(false);
    }

    setPrevScrollY(latest);
  });

  const variants = {
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      skew: 0,
    },
    hidden: {
      opacity: 0,
      scale: 1,
      rotate: 0,
      skew: 0,
    },
  };

  return (
    <Fragment>
      <motion.nav
        className="px-[20px] desktop:py-[12px] py-[6px] flex justify-between items-center fixed top-0 left-0 right-0 z-10 bg-white/80 backdrop-blur-[10px]"
        variants={variants}
        initial="visible"
        animate={shouldAnimate ? "hidden" : "visible"}
        transition={{
          type: "spring",
          duration: 0.4,
          bounce: 0.2,
          delay: 0,
        }}
      >
        <Link to="/" className="text-lg font-bold font-pre">
          <img src={logo} alt="logo" width={110} height={20} />
        </Link>
        <div className="flex items-center justify-end gap-[16px]">
          <motion.div
            initial={{
              backgroundColor: "transparent",
            }}
            whileHover={{
              opacity: 1,
              scale: 1,
              backgroundColor: "#f2f4f7",
            }}
            transition={{
              type: "spring",
              duration: 0.45,
              bounce: 0.25,
            }}
            className="rounded-[99px] desktop:w-[36px] desktop:h-[36px] w-[24px] h-[24px] flex items-center justify-center"
          >
            <Globe
              width={isMobile ? 24 : 24}
              height={isMobile ? 24 : 24}
              fill="#000000"
              hoverFill="#000000"
              className="cursor-pointer"
              currentLanguage={isKorean ? "ko" : "en"}
              onLanguageChange={handleLanguageChange}
            />
          </motion.div>

          {showContactButton && (
            <motion.div
              id="contact-us-button"
              whileHover={{
                opacity: 1,
                scale: 1,
                backgroundColor: "#f2f4f7",
              }}
              transition={{
                type: "spring",
                duration: 0.45,
                bounce: 0.25,
              }}
              className="cursor-pointer flex items-center justify-center tablet:px-[16px] tablet:py-[8px] px-[12px] py-[6px] rounded-[8px] bg-[#ffffff] border border-[#E6E6E6]"
              onClick={openModal}
            >
              <span
                className="font-eng-semibold tablet:text-[14px] text-[13px] leading-[1.5em]"
                style={{ letterSpacing: "-0.04em" }}
              >
                Contact us
              </span>
            </motion.div>
          )}
        </div>
      </motion.nav>
      <main>
        <Outlet />
      </main>
    </Fragment>
  );
}

export default Layout;
